import { useState, useRef, useEffect } from "react";
import { Icon } from "@iconify/react";
import { PlusIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { CheckIcon } from "lucide-react";
import { toast } from "react-toastify";
import { useQueries } from "@tanstack/react-query";
import {
  useGetAllComplianceProfiles,
  useCreateComplianceProfile,
  useUpdateComplianceProfile,
  useDeleteComplianceProfile,
  useCheckCompliance,
  useDownloadComplianceReport,
  useGetAllDevices,
  useGetAllLocations,
} from "../../queries/admin";
import { getDeviceParameters } from "../../api/admin";
import DaysSelector from "../../components/DaysSelector";
import { useIsAdmin } from "../../utils/auth";
import type {
  ComplianceProfile,
  ComplianceRuleDto,
  ComplianceCheckResult,
  SlidingWindowDays,
} from "../../types/compliance";

/* ────────────── Constants ────────────── */

const SLIDING_WINDOWS: SlidingWindowDays[] = [15, 30, 45, 60, 90];

/* ────────────── Action Menu ────────────── */

function ActionMenu({
  onInfo,
  onEdit,
  onDelete,
  infoDisabled = false,
  canManage = true,
}: {
  onInfo: () => void;
  onEdit: () => void;
  onDelete: () => void;
  infoDisabled?: boolean;
  canManage?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <Icon icon="mdi:dots-vertical" className="w-5 h-5 text-gray-500 dark:text-gray-400" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-36 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 py-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (infoDisabled) return;
              onInfo();
              setOpen(false);
            }}
            disabled={infoDisabled}
            title={infoDisabled ? "No sensor data available" : undefined}
            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
          >
            <Icon icon="mdi:information-outline" className="w-4 h-4" /> Info
          </button>
          {canManage && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
                setOpen(false);
              }}
              className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Icon icon="mdi:pencil-outline" className="w-4 h-4" /> Edit
            </button>
          )}
          {canManage && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
                setOpen(false);
              }}
              className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Icon icon="mdi:delete-outline" className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ────────────── Row Action Menu ────────────── */

function RowActionMenu({ onView }: { onView?: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <Icon icon="mdi:dots-vertical" className="w-4 h-4 text-gray-500 dark:text-gray-400" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-32 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 py-1">
          <button
            onClick={() => {
              onView?.();
              setOpen(false);
            }}
            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Icon icon="mdi:eye-outline" className="w-4 h-4" /> View
          </button>
        </div>
      )}
    </div>
  );
}

/* ────────────── Score Ring ────────────── */

function ScoreRing({ value, label }: { value: string; label: string }) {
  const isNumeric = !value.includes("/");
  const denominator = isNumeric ? 1 : Number(value.split("/")[1]);
  const numericValue = isNumeric
    ? Number(value)
    : denominator > 0
      ? (Number(value.split("/")[0]) / denominator) * 100
      : 0;

  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (numericValue / 100) * circumference;
  const color =
    numericValue >= 80 ? "text-green-500" : numericValue >= 50 ? "text-yellow-500" : "text-red-500";

  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-xs text-gray-500 dark:text-gray-400 text-center leading-tight">
        {label}
      </span>
      <div className="relative w-16 h-16 flex items-center justify-center">
        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
          <circle
            cx="32"
            cy="32"
            r={radius}
            fill="none"
            stroke="currentColor"
            className="text-gray-200 dark:text-gray-700"
            strokeWidth="3"
          />
          <circle
            cx="32"
            cy="32"
            r={radius}
            fill="none"
            stroke="currentColor"
            className={color}
            strokeWidth="3"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
          />
        </svg>
        <span className="absolute text-sm font-semibold text-gray-900 dark:text-white">
          {value}
        </span>
      </div>
    </div>
  );
}

/* ────────────── Sliding Window Selector ────────────── */

function WindowSelector({
  selected,
  onChange,
}: {
  selected: SlidingWindowDays;
  onChange: (d: SlidingWindowDays) => void;
}) {
  return (
    <DaysSelector
      options={SLIDING_WINDOWS.map((d) => ({ value: d, label: `${d}d` }))}
      selected={selected}
      onSelect={onChange}
    />
  );
}

/* ────────────── Profile Card ────────────── */

function ProfileCard({
  profile,
  checkResult,
  onInfo,
  onEdit,
  onDelete,
  canManage = true,
}: {
  profile: ComplianceProfile;
  checkResult?: ComplianceCheckResult;
  onInfo: () => void;
  onEdit: () => void;
  onDelete: () => void;
  canManage?: boolean;
}) {
  const paramScore = checkResult?.parameterScore ?? `0/${profile.rules.length}`;
  const overallScore = checkResult?.overallCompliance ?? 0;

  const lastTime = checkResult?.lastRecordedAt ? new Date(checkResult.lastRecordedAt) : null;
  const hoursAgo = lastTime ? (Date.now() - lastTime.getTime()) / (1000 * 60 * 60) : 0;
  const isStale = lastTime ? hoursAgo > 3 : false;
  const hasNoData = checkResult ? checkResult.totalDataPoints === 0 : false;

  return (
    <div
      onClick={hasNoData ? undefined : onInfo}
      title={hasNoData ? "No sensor data available" : undefined}
      className={`bg-white dark:bg-gray-800 border rounded-xl p-5 min-w-[280px] flex-1 max-w-md ${
        hasNoData ? "cursor-not-allowed" : "cursor-pointer"
      } ${
        isStale || hasNoData
          ? "border-amber-300 dark:border-amber-700"
          : "border-gray-200 dark:border-gray-700"
      }`}
    >
      <div className="flex items-start justify-between mb-1">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{profile.name}</h3>
          {profile.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
              {profile.description}
            </p>
          )}
        </div>
        <ActionMenu
          onInfo={onInfo}
          onEdit={onEdit}
          onDelete={onDelete}
          infoDisabled={hasNoData}
          canManage={canManage}
        />
      </div>

      {/* Stale / No Data indicator on card */}
      {hasNoData && !lastTime && (
        <div className="flex items-center gap-1.5 mt-2 px-2 py-1.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
          <Icon icon="mdi:alert-outline" className="w-3.5 h-3.5 text-amber-500 shrink-0" />
          <p className="text-xs text-amber-700 dark:text-amber-400">No sensor data available</p>
        </div>
      )}
      {hasNoData && lastTime && (
        <div className="flex items-center gap-1.5 mt-2 px-2 py-1.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
          <Icon icon="mdi:clock-alert-outline" className="w-3.5 h-3.5 text-amber-500 shrink-0" />
          <p className="text-xs text-amber-700 dark:text-amber-400 truncate">
            No data in selected window. Last received:{" "}
            {lastTime.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
          </p>
        </div>
      )}
      {!hasNoData && isStale && lastTime && (
        <div className="flex items-center gap-1.5 mt-2 px-2 py-1.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
          <Icon icon="mdi:clock-alert-outline" className="w-3.5 h-3.5 text-amber-500 shrink-0" />
          <p className="text-xs text-amber-700 dark:text-amber-400 truncate">
            Data stopped since{" "}
            {lastTime.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
          </p>
        </div>
      )}

      <div className="flex items-center justify-between mt-3">
        {hasNoData ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">No values recorded yet</p>
        ) : (
          <div className="flex items-center gap-6">
            <ScoreRing value={paramScore} label="Parameter Score" />
            <ScoreRing value={String(overallScore)} label="Overall Score" />
          </div>
        )}
        <div className="text-right">
          <p className="text-xs text-gray-500 dark:text-gray-400">Rules</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white">{profile.rules.length}</p>
        </div>
      </div>
    </div>
  );
}

/* ────────────── Parameter Select (dashboard-style dropdown) ────────────── */

function ParameterSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  includeUnknown,
}: {
  value: string;
  onChange: (name: string) => void;
  options: { name: string; unit: string }[];
  placeholder: string;
  disabled?: boolean;
  includeUnknown?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = options.find((p) => p.name.toLowerCase() === value?.toLowerCase());
  const buttonLabel = value
    ? selected
      ? `${selected.name}${selected.unit ? ` (${selected.unit})` : ""}`
      : `${value} (not in selected devices)`
    : placeholder;

  return (
    <div className="relative w-full" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="relative w-full h-10 cursor-pointer rounded-lg bg-white dark:bg-gray-800 py-1.5 pl-3 pr-8 text-left text-gray-900 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-primary text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span
          className={`block truncate font-medium text-sm ${
            !value ? "text-gray-500 dark:text-gray-400" : ""
          }`}
        >
          {buttonLabel}
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </span>
      </button>

      {open && !disabled && (
        <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-[#1F2C33] py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-600 dark:border dark:border-gray-600 focus:outline-none sm:text-sm">
          {options.length === 0 && !includeUnknown && (
            <li className="py-2 px-3 text-sm text-gray-500 dark:text-gray-400">
              No parameters available
            </li>
          )}
          {includeUnknown && (
            <li
              onClick={() => {
                onChange(value);
                setOpen(false);
              }}
              className="relative cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 select-none py-2 px-3 text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700"
            >
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded border bg-primary border-primary flex-shrink-0 flex items-center justify-center">
                  <CheckIcon className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm">{value} (not in selected devices)</span>
              </div>
            </li>
          )}
          {options.map((p, index) => {
            const isChecked = p.name.toLowerCase() === value?.toLowerCase();
            return (
              <li
                key={p.name}
                onClick={() => {
                  onChange(p.name);
                  setOpen(false);
                }}
                className={`relative cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 select-none py-2 px-3 text-gray-900 dark:text-white ${
                  isChecked ? "bg-gray-50/50 dark:bg-gray-700/50" : ""
                } ${
                  index !== options.length - 1
                    ? "border-b border-gray-100 dark:border-gray-700"
                    : ""
                }`}
              >
                <div className="flex items-center gap-2">
                  <div
                    className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                      isChecked
                        ? "bg-primary border-primary"
                        : "border-gray-300 dark:border-gray-500"
                    }`}
                  >
                    {isChecked && <CheckIcon className="w-3 h-3 text-white" />}
                  </div>
                  <span className="text-sm">
                    {p.name}
                    {p.unit ? ` (${p.unit})` : ""}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ────────────── Create / Edit Modal ────────────── */

function ProfileModal({
  profile,
  onClose,
  onSave,
  isSaving,
}: {
  profile: ComplianceProfile | null; // null = create mode
  onClose: () => void;
  onSave: (
    name: string,
    description: string,
    rules: ComplianceRuleDto[],
    deviceIds: string[],
  ) => void;
  isSaving: boolean;
}) {
  const [name, setName] = useState(profile?.name ?? "");
  const [description, setDescription] = useState(profile?.description ?? "");
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>(profile?.deviceIds ?? []);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const { data: allDevices } = useGetAllDevices();
  const { data: allLocations } = useGetAllLocations();

  // Profiles don't store a locationId directly, only deviceIds. On edit, infer
  // the location from one of the profile's existing devices once they've
  // loaded, so the devices checklist isn't shown as empty/unselected.
  useEffect(() => {
    if (!profile || selectedLocationId || !allDevices?.length) return;
    const existingDevice = allDevices.find((d: any) => selectedDeviceIds.includes(d.id));
    if (existingDevice?.locationId) {
      setSelectedLocationId(existingDevice.locationId);
    }
  }, [profile, allDevices, selectedDeviceIds, selectedLocationId]);
  const [rules, setRules] = useState<ComplianceRuleDto[]>(
    profile?.rules?.map((r) => ({
      parameterName: r.parameterName,
      unit: r.unit,
      minValue: r.minValue,
      maxValue: r.maxValue,
    })) ?? [{ parameterName: "", unit: "", minValue: null, maxValue: null }],
  );

  // Fetch parameters for each selected device and build a de-duped union.
  // The backend matches compliance rules to device data by parameter name
  // (or identifier), so the dropdown must come from real device parameters.
  const parameterQueries = useQueries({
    queries: selectedDeviceIds.map((deviceId) => ({
      queryKey: ["parameters", "device", deviceId],
      queryFn: getDeviceParameters,
      enabled: !!deviceId,
      staleTime: 1000 * 60 * 5,
    })),
  });
  const isLoadingParams = parameterQueries.some((q) => q.isLoading);
  const availableParametersMap = new Map<string, { name: string; unit: string }>();
  parameterQueries.forEach((q) => {
    (q.data ?? []).forEach((p: any) => {
      const key = p.name?.toLowerCase();
      if (key && !availableParametersMap.has(key)) {
        availableParametersMap.set(key, { name: p.name, unit: p.unit ?? "" });
      }
    });
  });
  const availableParameters = Array.from(availableParametersMap.values());

  const addRule = () =>
    setRules([...rules, { parameterName: "", unit: "", minValue: null, maxValue: null }]);
  const removeRule = (i: number) => setRules(rules.filter((_, idx) => idx !== i));
  const updateRule = (i: number, field: keyof ComplianceRuleDto, value: any) => {
    const updated = [...rules];
    (updated[i] as any)[field] = value;
    setRules(updated);
  };
  const selectRuleParameter = (i: number, parameterName: string) => {
    const match = availableParameters.find(
      (p) => p.name.toLowerCase() === parameterName.toLowerCase(),
    );
    setRules((prev) => {
      const updated = [...prev];
      updated[i] = {
        ...updated[i],
        parameterName,
        unit: match?.unit ?? updated[i].unit,
      };
      return updated;
    });
  };

  const toggleDevice = (deviceId: string) => {
    setSelectedDeviceIds((prev) =>
      prev.includes(deviceId) ? prev.filter((id) => id !== deviceId) : [...prev, deviceId],
    );
  };

  const filteredDevices = (allDevices ?? []).filter(
    (d: any) => {
      const matchesSearch =
        d.name?.toLowerCase().includes(deviceSearch.toLowerCase()) ||
        d.identifier?.toLowerCase().includes(deviceSearch.toLowerCase());
      const matchesLocation = !selectedLocationId || d.locationId === selectedLocationId;
      return matchesSearch && matchesLocation;
    },
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedDeviceIds.length === 0) {
      toast.error("Please select at least one device");
      return;
    }
    const filteredRules = rules.filter((r) => r.parameterName.trim());
    const validRules = filteredRules.filter(
      (r) =>
        (r.minValue !== undefined && r.minValue !== null) ||
        (r.maxValue !== undefined && r.maxValue !== null),
    );
    if (validRules.length === 0) {
      toast.error("Each rule must have at least a min or max value");
      return;
    }
    onSave(name, description, validRules, selectedDeviceIds);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {profile ? "Edit Compliance Profile" : "New Compliance Profile"}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Icon icon="mdi:close" className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-5">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Profile Name *
              </label>
              <input
                required
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. WELL v2, WHO Standards"
                className="w-full px-3 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this compliance standard"
                rows={2}
                className="w-full px-3 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            {/* Location Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Location *
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Pick a location to scope this compliance profile
              </p>
              <div className="relative">
                <select
                  value={selectedLocationId}
                  onChange={(e) => {
                    const newLocationId = e.target.value;
                    setSelectedLocationId(newLocationId);
                    // Drop only devices that don't belong to the new location,
                    // instead of wiping the whole selection — otherwise a stray
                    // location change during edit silently unlinks devices that
                    // already had rules/history configured against them.
                    setSelectedDeviceIds((prev) =>
                      prev.filter(
                        (id) =>
                          (allDevices ?? []).find((d: any) => d.id === id)?.locationId ===
                          newLocationId,
                      ),
                    );
                  }}
                  className="w-full h-10 pl-3 pr-10 py-1.5 border-0 rounded-lg appearance-none bg-white dark:bg-gray-800 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 dark:text-gray-200 text-sm sm:leading-6 cursor-pointer"
                >
                  <option value="">Select a location</option>
                  {(allLocations ?? []).map((loc: any) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <svg className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Device Selection */}
            {selectedLocationId ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Devices *
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Select the devices this compliance profile applies to
                </p>
                <div className="relative mb-2">
                  <input
                    type="text"
                    placeholder="Search devices..."
                    value={deviceSearch}
                    onChange={(e) => setDeviceSearch(e.target.value)}
                    className="w-full pl-4 pr-10 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <Icon
                    icon="mdi:magnify"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700/50">
                  {filteredDevices.length === 0 ? (
                    <p className="px-3 py-3 text-sm text-gray-400">
                      No devices found in this location
                    </p>
                  ) : (
                    filteredDevices.map((device: any) => (
                      <label
                        key={device.id}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedDeviceIds.includes(device.id)}
                          onChange={() => toggleDevice(device.id)}
                          className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {device.name}
                          </p>
                          {device.identifier && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {device.identifier}
                            </p>
                          )}
                        </div>
                      </label>
                    ))
                  )}
                </div>
                {selectedDeviceIds.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                    {selectedDeviceIds.length} device{selectedDeviceIds.length !== 1 ? "s" : ""}{" "}
                    selected
                  </p>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Devices *
                </label>
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Select a location above to see its devices.
                </p>
              </div>
            )}

            {/* Rules */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Parameters & Thresholds
                </label>
                <button
                  type="button"
                  onClick={addRule}
                  className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 font-medium"
                >
                  <PlusIcon className="w-3.5 h-3.5" /> Add Parameter
                </button>
              </div>

              {selectedDeviceIds.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
                  Select at least one device above to choose parameters.
                </p>
              )}
              {selectedDeviceIds.length > 0 &&
                !isLoadingParams &&
                availableParameters.length === 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
                    The selected device(s) have no parameters defined.
                  </p>
                )}
              <div className="space-y-3">
                {rules.map((rule, i) => {
                  // Ensure an existing rule whose parameter isn't in the current
                  // device selection is still visible and preserved in the dropdown.
                  const hasCurrentInList = !!availableParameters.find(
                    (p) => p.name.toLowerCase() === rule.parameterName?.toLowerCase(),
                  );
                  return (
                    <div
                      key={i}
                      className="flex items-start gap-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3"
                    >
                      <div className="flex-1 grid grid-cols-4 gap-2">
                        <div className="col-span-2">
                          <ParameterSelect
                            value={rule.parameterName}
                            onChange={(name) => selectRuleParameter(i, name)}
                            disabled={selectedDeviceIds.length === 0}
                            placeholder={
                              selectedDeviceIds.length === 0
                                ? "Select devices first"
                                : isLoadingParams
                                  ? "Loading parameters..."
                                  : "Select parameter"
                            }
                            options={availableParameters}
                            includeUnknown={!!rule.parameterName && !hasCurrentInList}
                          />
                        </div>
                        <input
                          type="text"
                          placeholder="Unit"
                          value={rule.unit}
                          onChange={(e) => updateRule(i, "unit", e.target.value)}
                          className="px-2.5 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div /> {/* spacer for alignment */}
                        <input
                          type="number"
                          step="any"
                          placeholder="Min value"
                          value={rule.minValue ?? ""}
                          onChange={(e) =>
                            updateRule(
                              i,
                              "minValue",
                              e.target.value === "" ? null : Number(e.target.value),
                            )
                          }
                          className="px-2.5 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="number"
                          step="any"
                          placeholder="Max value"
                          value={rule.maxValue ?? ""}
                          onChange={(e) =>
                            updateRule(
                              i,
                              "maxValue",
                              e.target.value === "" ? null : Number(e.target.value),
                            )
                          }
                          className="px-2.5 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      {rules.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRule(i)}
                          className="mt-2 p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                        >
                          <Icon icon="mdi:close" className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? "Saving..." : profile ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ────────────── Delete Confirmation ────────────── */

function DeleteConfirm({
  profileName,
  onCancel,
  onConfirm,
  isDeleting,
}: {
  profileName: string;
  onCancel: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Icon icon="mdi:delete-outline" className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Profile</h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Are you sure you want to delete <strong>{profileName}</strong>? This action cannot be
          undone.
        </p>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────── Info View (Compliance Detail) ────────────── */

function ComplianceInfoView({
  profile,
  onBack,
}: {
  profile: ComplianceProfile;
  onBack: () => void;
}) {
  const [days, setDays] = useState<SlidingWindowDays>(30);
  const { data: checkResult, isLoading, isError } = useCheckCompliance(profile.id, days);
  const downloadReport = useDownloadComplianceReport();

  const handleDownload = () => {
    downloadReport.mutate({ profileId: profile.id, days });
  };

  return (
    <div className="h-full flex flex-col gap-4">
      {/* Back button */}
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors self-start"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Compliance
      </button>

      <div className="flex-1 bg-white dark:bg-gray-900 p-4 rounded-lg">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{profile.name}</h1>
          {profile.description && (
            <p className="text-gray-600 dark:text-gray-400 mt-1">{profile.description}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <WindowSelector selected={days} onChange={setDays} />
          <button
            onClick={handleDownload}
            disabled={downloadReport.isPending || !checkResult || checkResult.totalDataPoints === 0}
            title={
              checkResult && checkResult.totalDataPoints === 0
                ? "No values recorded yet"
                : undefined
            }
            className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon icon="mdi:download" className="w-4 h-4" />
            {downloadReport.isPending ? "Generating..." : "Download Report"}
          </button>
        </div>
      </div>

      {/* Loading / Error */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Icon icon="mdi:loading" className="w-8 h-8 text-blue-500 animate-spin" />
          <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
            Checking compliance for last {days} days...
          </span>
        </div>
      )}

      {isError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-6">
          <p className="text-sm text-red-600 dark:text-red-400">
            Failed to load compliance data. Make sure your devices have data in the selected period.
          </p>
        </div>
      )}

      {checkResult && (
        <>
          {/* Stale Data Banner */}
          {(() => {
            if (!checkResult.lastRecordedAt) return null;
            const lastTime = new Date(checkResult.lastRecordedAt);
            const hoursAgo = (Date.now() - lastTime.getTime()) / (1000 * 60 * 60);
            if (hoursAgo <= 3) return null;
            return (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-4 mb-6 flex items-start gap-3">
                <Icon icon="mdi:alert-outline" className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    Data stopped receiving since{" "}
                    {lastTime.toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    The compliance you are viewing is based on the last received values. Live data has
                    not been received for over {Math.floor(hoursAgo)} hour{Math.floor(hoursAgo) !== 1 ? "s" : ""}.
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Last Recorded Info */}
          {checkResult.lastRecordedAt && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Last data received:{" "}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {new Date(checkResult.lastRecordedAt).toLocaleString(undefined, {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
              </span>
            </p>
          )}

          {/* Compliance Statistics */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Compliance Statistics
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                (Last {days} days)
              </span>
            </h3>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-gray-200 dark:divide-gray-700">
                <div className="px-4 py-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Compliance Status</p>
                  <p
                    className={`text-sm font-medium ${
                      checkResult.stats.complianceStatus === "Compliant"
                        ? "text-green-600 dark:text-green-400"
                        : checkResult.stats.complianceStatus === "No Data"
                          ? "text-gray-500 dark:text-gray-400"
                          : "text-red-600 dark:text-red-400"
                    }`}
                  >
                    {checkResult.stats.complianceStatus}
                  </p>
                </div>
                <div className="px-4 py-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Locations Impacted
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {checkResult.stats.locationsImpacted}
                  </p>
                </div>
                <div className="px-4 py-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Devices Coverage</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {checkResult.stats.devicesCoverage}
                  </p>
                </div>
                <div className="px-4 py-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Violations</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {checkResult.stats.violations}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Overall Scores */}
          <div className="mb-6 flex items-center gap-8">
            {checkResult.totalDataPoints === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                No values recorded yet
              </p>
            ) : (
              <>
                <ScoreRing value={checkResult.parameterScore} label="Parameter Score" />
                <ScoreRing
                  value={String(checkResult.overallCompliance)}
                  label="Overall Compliance"
                />
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  <p>
                    Total data points:{" "}
                    <strong className="text-gray-900 dark:text-white">
                      {checkResult.totalDataPoints.toLocaleString()}
                    </strong>
                  </p>
                  <p>
                    Period: {new Date(checkResult.startDate).toLocaleDateString()} -{" "}
                    {new Date(checkResult.endDate).toLocaleDateString()}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Parameter Rules Results */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Parameter Compliance
            </h3>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Parameter
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Unit
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Min
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Max
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Avg Value
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Score
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Violations
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Devices
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {checkResult.rules.map((rule) => (
                    <tr key={rule.ruleId}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {rule.parameterName}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {rule.unit}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {rule.minValue ?? "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {rule.maxValue ?? "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {rule.averageValue != null ? rule.averageValue : "-"}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {rule.compliancePercent}%
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {rule.violations}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {rule.matchedDeviceCount}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            rule.noData
                              ? "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                              : rule.compliancePercent === 100
                                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                          }`}
                        >
                          {rule.noData
                            ? "No Data"
                            : rule.compliancePercent === 100
                              ? "Compliant"
                              : "Non-Compliant"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Locations */}
          {checkResult.locations.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Locations
              </h3>
              <div className="space-y-2">
                {checkResult.locations.map((loc) => (
                  <div
                    key={loc.id}
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-5 py-3 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-6 flex-1">
                      <span className="text-sm font-medium text-gray-900 dark:text-white min-w-[120px]">
                        {loc.name}
                      </span>
                      <span
                        className={`text-sm ${
                          loc.status === "Compliant"
                            ? "text-green-600 dark:text-green-400"
                            : "text-red-600 dark:text-red-400"
                        }`}
                      >
                        {loc.status}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Devices: {loc.deviceCount}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Violations: {loc.violations}
                      </span>
                    </div>
                    <RowActionMenu />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Devices */}
          {checkResult.devices.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Devices</h3>
              <div className="space-y-2">
                {checkResult.devices.map((dev) => (
                  <div
                    key={dev.id}
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-5 py-3 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-6 flex-1">
                      <span className="text-sm font-medium text-gray-900 dark:text-white min-w-[120px]">
                        {dev.name}
                      </span>
                      <span
                        className={`text-sm ${
                          dev.status === "Compliant"
                            ? "text-green-600 dark:text-green-400"
                            : "text-red-600 dark:text-red-400"
                        }`}
                      >
                        {dev.status}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Location: {dev.locationName}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Violations: {dev.violations}
                      </span>
                    </div>
                    <RowActionMenu />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Data */}
          {checkResult.totalDataPoints === 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                No matching sensor data found in the last {days} days. Make sure your device
                parameters match the compliance rule names.
              </p>
            </div>
          )}
          {checkResult.totalDataPoints > 0 && checkResult.noDataRuleCount > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                {checkResult.noDataRuleCount} parameter rule{checkResult.noDataRuleCount !== 1 ? "s have" : " has"} no
                matching sensor data in the last {days} days. These rules are excluded from compliance scores.
              </p>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}

/* ────────────── Main Page ────────────── */

export default function CompliancePage() {
  const isAdmin = useIsAdmin();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProfile, setSelectedProfile] = useState<ComplianceProfile | null>(null);
  const [modalProfile, setModalProfile] = useState<ComplianceProfile | null | undefined>(undefined); // undefined = closed, null = create
  const [deleteTarget, setDeleteTarget] = useState<ComplianceProfile | null>(null);
  const [globalDays, setGlobalDays] = useState<SlidingWindowDays>(30);

  // API hooks
  const { data: profiles, isLoading } = useGetAllComplianceProfiles();
  const createMutation = useCreateComplianceProfile();
  const updateMutation = useUpdateComplianceProfile();
  const deleteMutation = useDeleteComplianceProfile();

  // Check compliance for all profiles (for the cards)
  const filteredProfiles = (profiles ?? []).filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // We fetch compliance check for each profile on the card view
  // This is handled individually per card via the hook

  const handleSave = (
    name: string,
    description: string,
    rules: ComplianceRuleDto[],
    deviceIds: string[],
  ) => {
    if (modalProfile === null) {
      // Create
      createMutation.mutate(
        { name, description, rules, deviceIds },
        { onSuccess: () => setModalProfile(undefined) },
      );
    } else if (modalProfile) {
      // Update
      updateMutation.mutate(
        { id: modalProfile.id, data: { name, description, rules, deviceIds } },
        { onSuccess: () => setModalProfile(undefined) },
      );
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  };

  // If viewing a specific profile, show the detail view
  if (selectedProfile) {
    return <ComplianceInfoView profile={selectedProfile} onBack={() => setSelectedProfile(null)} />;
  }

  return (
    <div className="flex-1 flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Compliance Manager</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Set and track parameter limits to ensure compliance with standards or custom thresholds.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setModalProfile(null)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            New Compliance
          </button>
        )}
      </div>

      {/* Search & Actions */}
      <div className="flex items-center gap-4 justify-between flex-wrap">
        <div className="relative flex-1 max-w-[50%]">
          <input
            type="text"
            placeholder="Search Compliances"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-4 pr-10 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <Icon
            icon="mdi:magnify"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
          />
        </div>
        <WindowSelector selected={globalDays} onChange={setGlobalDays} />
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Icon icon="mdi:loading" className="w-8 h-8 text-blue-500 animate-spin" />
          <span className="ml-3 text-sm text-gray-500">Loading profiles...</span>
        </div>
      )}

      {/* Profiles */}
      {!isLoading && (
        <div className="flex-1 flex flex-col">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Compliance Profiles
          </h2>
          {filteredProfiles.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center bg-white dark:bg-[#111B21] rounded-lg border border-gray-200 dark:border-gray-700">
              <img
                src="/sidenav/reports.svg"
                alt=""
                className="mx-auto h-20 w-20 opacity-60 dark:invert"
              />
              <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                {profiles?.length === 0
                  ? "No compliance profiles"
                  : "No matching profiles"}
              </h3>
              <p className="mt-2 text-base text-gray-500 dark:text-gray-400">
                {profiles?.length === 0
                  ? "Get started by creating a new compliance profile."
                  : "No compliance profiles match your search."}
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-4">
              {filteredProfiles.map((profile) => (
                <ProfileCardWithCheck
                  key={profile.id}
                  profile={profile}
                  days={globalDays}
                  onInfo={() => setSelectedProfile(profile)}
                  onEdit={() => setModalProfile(profile)}
                  onDelete={() => setDeleteTarget(profile)}
                  canManage={isAdmin}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {modalProfile !== undefined && (
        <ProfileModal
          profile={modalProfile}
          onClose={() => setModalProfile(undefined)}
          onSave={handleSave}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <DeleteConfirm
          profileName={deleteTarget.name}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

/* ────────────── Profile Card with Compliance Check ────────────── */

function ProfileCardWithCheck({
  profile,
  days,
  onInfo,
  onEdit,
  onDelete,
  canManage = true,
}: {
  profile: ComplianceProfile;
  days: SlidingWindowDays;
  onInfo: () => void;
  onEdit: () => void;
  onDelete: () => void;
  canManage?: boolean;
}) {
  const { data: checkResult } = useCheckCompliance(profile.id, days);

  return (
    <ProfileCard
      profile={profile}
      checkResult={checkResult}
      onInfo={onInfo}
      onEdit={onEdit}
      onDelete={onDelete}
      canManage={canManage}
    />
  );
}
